import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../services/firebase";

const clamp0 = (n) => (n < 0 ? 0 : n);

function firstNonEmpty(...values) {
  for (const v of values) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function getAccountName(customer = {}) {
  return firstNonEmpty(
    customer.accountName,
    customer.accountsName,
    customer.accountContactName,
  );
}

function getAccountsEmail(customer = {}) {
  return firstNonEmpty(
    customer.accountsEmail,
    customer.accountEmail,
    customer.accounts?.email,
  );
}

function getProjectName(site = {}) {
  return firstNonEmpty(site.projectName, site.project, site.projectTitle);
}

function getPoNumber(site = {}) {
  return firstNonEmpty(
    site.poNumber,
    site.po,
    site.purchaseOrder,
    site.purchaseOrderNumber,
  );
}

function getSiteAccessNotes(site = {}) {
  return firstNonEmpty(
    site.siteAccessNotes,
    site.accessNotes,
    site.siteNotes,
    site.driverNotes,
  );
}

function getBookingContactName(site = {}) {
  return firstNonEmpty(
    site.bookingContactName,
    site.contactName,
    site.bookingContact?.name,
    site.bookingContactDetails?.name,
    site.booking?.name,
  );
}

function getBookingContactEmail(site = {}) {
  return firstNonEmpty(
    site.bookingContactEmail,
    site.contactEmail,
    site.bookingContact?.email,
    site.bookingContactDetails?.email,
    site.booking?.email,
  );
}

function getBookingContactPhone(site = {}) {
  return firstNonEmpty(
    site.bookingContactPhone,
    site.contactPhone,
    site.bookingContact?.phone,
    site.bookingContactDetails?.phone,
    site.booking?.phone,
  );
}

function getSiteSearchBlob(site = {}) {
  return [
    site.label,
    site.address,
    getProjectName(site),
    getPoNumber(site),
    getSiteAccessNotes(site),
    getBookingContactName(site),
    getBookingContactEmail(site),
    getBookingContactPhone(site),
  ]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");
}

export default function CustomersTab({ company }) {
  const [customers, setCustomers] = useState([]);
  const [bins, setBins] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [filter, setFilter] = useState("");

  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const openNew = () => {
    setEditing({
      name: "",
      email: "",
      phone: "",
      accountName: "",
      accountsEmail: "",
      companyId: company?.id || "",
      sites: [newBlankSite()],
    });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing({
      ...c,
      accountName: getAccountName(c),
      accountsEmail: getAccountsEmail(c),
      sites:
        Array.isArray(c.sites) && c.sites.length
          ? c.sites.map((s) => ({
              ...s,
              projectName: getProjectName(s),
              poNumber: getPoNumber(s),
              siteAccessNotes: getSiteAccessNotes(s),
              bookingContactName: getBookingContactName(s),
              bookingContactEmail: getBookingContactEmail(s),
              bookingContactPhone: getBookingContactPhone(s),
            }))
          : [newBlankSite()],
    });
    setShowForm(true);
  };

  useEffect(() => {
    if (!company?.id) return;
    const qy = query(
      collection(db, "customers"),
      where("companyId", "==", company.id),
    );
    return onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
      setCustomers(list);
    });
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id) return;
    const qy = query(
      collection(db, "bins"),
      where("companyId", "==", company.id),
    );
    return onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
      setBins(list);
    });
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id) return;
    const qy = query(
      collection(db, "bin_loans"),
      where("companyId", "==", company.id),
    );
    return onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLedger(list);
    });
  }, [company?.id]);

  const rows = useMemo(() => {
    const agg = {};
    ledger.forEach((x) => {
      const cid = String(x.customerId || "");
      const bid = String(x.binId || "");
      const qty = Number(x.qty || 0);
      if (!cid || !bid || !qty) return;
      agg[cid] = agg[cid] || {};
      const cur = agg[cid][bid] || 0;
      const next =
        x.direction === "out"
          ? cur + qty
          : x.direction === "in"
            ? cur - qty
            : cur;
      agg[cid][bid] = next;
    });

    return customers.map((c) => {
      const byBin = agg[c.id] || {};
      const cols = {};
      let total = 0;
      bins.forEach((b) => {
        const v = clamp0(Number(byBin[b.id] || 0));
        cols[b.id] = v;
        total += v;
      });
      return { ...c, _cols: cols, _total: total };
    });
  }, [customers, bins, ledger]);

  const filtered = useMemo(() => {
    const s = filter.trim().toLowerCase();
    if (!s) {
      return rows.map((r) => ({
        ...r,
        _matchedSites: r.sites || [],
        _matchedSiteCount: Array.isArray(r.sites) ? r.sites.length : 0,
        _baseMatched: false,
        _siteMatched: false,
      }));
    }

    return rows
      .map((r) => {
        const baseFields = [
          r.name,
          r.address,
          r.email,
          r.phone,
          getAccountName(r),
          getAccountsEmail(r),
        ].map((x) => String(x || "").toLowerCase());

        const baseMatched = baseFields.some((x) => x.includes(s));
        const matchedSites = (r.sites || []).filter((site) =>
          getSiteSearchBlob(site).includes(s),
        );
        const siteMatched = matchedSites.length > 0;

        if (!baseMatched && !siteMatched) return null;

        return {
          ...r,
          _matchedSites: siteMatched ? matchedSites : r.sites || [],
          _matchedSiteCount: matchedSites.length,
          _baseMatched: baseMatched,
          _siteMatched: siteMatched,
        };
      })
      .filter(Boolean);
  }, [rows, filter]);

  async function removeCustomer(id) {
    if (!window.confirm("Delete this customer?")) return;
    await deleteDoc(doc(db, "customers", id));
  }

  async function saveCustomer(data) {
    const payload = normalizeCustomerForSave(data, company?.id);
    if (!payload.name) {
      alert("Please enter a customer name.");
      return;
    }

    try {
      if (data.id) {
        await updateDoc(doc(db, "customers", data.id), payload);
      } else {
        await addDoc(collection(db, "customers"), payload);
      }
      setShowForm(false);
      setEditing(null);
    } catch (err) {
      console.error(err);
      alert("Failed to save customer: " + err.message);
    }
  }

  return (
    <div className="space-y-4 text-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400 sm:w-72"
          placeholder="Search customers, sites, project, PO, accounts..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button
          className="rounded-lg border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-black"
          onClick={openNew}
        >
          New customer
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr className="border-b border-gray-200">
              <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Customer
              </th>
              <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Accounts
              </th>
              <th className="p-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">
                Sites
              </th>
              {bins.map((b) => (
                <th
                  key={b.id}
                  className="p-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500"
                >
                  {b.name}
                </th>
              ))}
              <th className="p-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">
                Total
              </th>
              <th className="p-3" />
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {filtered.map((r) => (
              <CustomerRow
                key={r.id}
                customer={r}
                bins={bins}
                activeFilter={filter}
                onEdit={() => openEdit(r)}
                onDelete={() => removeCustomer(r.id)}
              />
            ))}

            {filtered.length === 0 && (
              <tr>
                <td
                  className="p-6 text-center text-sm text-gray-500"
                  colSpan={5 + bins.length}
                >
                  No customers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <CustomerFormDialog
          initial={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={saveCustomer}
        />
      )}
    </div>
  );
}

function CustomerRow({ customer, bins, onEdit, onDelete, activeFilter = "" }) {
  const hasActiveFilter = Boolean(activeFilter.trim());
  const visibleSites = hasActiveFilter
    ? customer._matchedSites || []
    : customer.sites || [];
  const matchCount = customer._matchedSiteCount || 0;
  const shouldAutoOpen = hasActiveFilter && customer._siteMatched;
  const [open, setOpen] = useState(shouldAutoOpen);

  useEffect(() => {
    if (shouldAutoOpen) setOpen(true);
  }, [shouldAutoOpen]);

  const totalSiteCount = (customer.sites || []).length;

  return (
    <>
      <tr className="align-top transition odd:bg-white even:bg-gray-50/40 hover:bg-emerald-50/40">
        <td className="p-4">
          <div className="flex items-start gap-3">
            <button
              className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Collapse" : "Expand"}
              title={open ? "Collapse" : "Expand"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            <div className="min-w-0">
              <div className="break-words text-sm font-semibold text-gray-900">
                {customer.name}
              </div>

              {(customer.phone || customer.email || (hasActiveFilter && customer._siteMatched)) && (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  {customer.phone && (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5">
                      {customer.phone}
                    </span>
                  )}
                  {customer.email && (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 break-all">
                      {customer.email}
                    </span>
                  )}
                  {hasActiveFilter && customer._siteMatched && (
                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                      {matchCount} matching site{matchCount === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>

        <td className="p-4">
          <div className="space-y-1 text-xs text-gray-600">
            <div className="font-medium text-gray-800">
              {getAccountName(customer) || "-"}
            </div>
            <div className="break-all">{getAccountsEmail(customer) || "-"}</div>
          </div>
        </td>

        <td className="p-4 text-center">
          {totalSiteCount === 0 ? (
            <span className="text-xs text-gray-400">-</span>
          ) : (
            <span className="inline-flex min-w-[2.25rem] justify-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-700">
              {totalSiteCount}
            </span>
          )}
          {hasActiveFilter && customer._siteMatched && matchCount !== totalSiteCount && (
            <div className="mt-1 text-[11px] font-medium text-emerald-700">
              {matchCount} matching
            </div>
          )}
        </td>

        {bins.map((b) => (
          <td key={b.id} className="p-4 text-center text-sm tabular-nums text-gray-800">
            <span className="inline-flex min-w-[2rem] justify-center rounded-lg border border-gray-200 bg-gray-50 px-2 py-1">
              {customer._cols?.[b.id] ?? 0}
            </span>
          </td>
        ))}

        <td className="p-4 text-center text-sm tabular-nums">
          <span className="inline-flex min-w-[2.5rem] justify-center rounded-lg bg-gray-900 px-2.5 py-1 font-semibold text-white">
            {customer._total}
          </span>
        </td>

        <td className="whitespace-nowrap p-4 text-right">
          <div className="flex justify-end gap-2">
            <button
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm transition hover:border-gray-400 hover:bg-gray-50 sm:text-sm"
              onClick={onEdit}
            >
              Edit
            </button>
            <button
              className="rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-50 sm:text-sm"
              onClick={onDelete}
            >
              Delete
            </button>
          </div>
        </td>
      </tr>

      {open && (
        <tr className="border-t border-gray-100 bg-gradient-to-b from-gray-50/70 to-white">
          <td className="p-4" colSpan={5 + bins.length}>
            <div className="space-y-4">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Customer accounts details
                    </div>
                    <div className="text-xs text-gray-500">
                      Finance and accounts contact for this customer
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                      Account name
                    </div>
                    <div className="mt-1 text-sm text-gray-900">
                      {getAccountName(customer) || "-"}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                      Accounts email
                    </div>
                    <div className="mt-1 break-all text-sm text-gray-900">
                      {getAccountsEmail(customer) || "-"}
                    </div>
                  </div>
                </div>
              </div>

              {visibleSites.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
                  No sites added yet.
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {visibleSites.map((s) => (
                    <div
                      key={s.id}
                      className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {s.label || "Site"}
                          </div>
                          {s.address && (
                            <div className="mt-1 text-xs leading-5 text-gray-600">
                              {s.address}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 space-y-2 text-sm">
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wide text-gray-500">
                            Project name
                          </div>
                          <div className="mt-1 text-gray-900">
                            {getProjectName(s) || "-"}
                          </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wide text-gray-500">
                            PO
                          </div>
                          <div className="mt-1 text-gray-900">
                            {getPoNumber(s) || "-"}
                          </div>
                        </div>

                        <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wide text-blue-700">
                            Site access notes
                          </div>
                          <div className="mt-1 whitespace-pre-wrap text-sm text-blue-900">
                            {getSiteAccessNotes(s) || "-"}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 border-t border-gray-100 pt-4">
                        <div className="mb-3 text-sm font-semibold text-gray-800">
                          Booking contact details
                        </div>

                        <div className="space-y-2">
                          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="text-[11px] uppercase tracking-wide text-gray-500">
                              Name
                            </div>
                            <div className="mt-1 text-sm text-gray-900">
                              {getBookingContactName(s) || "-"}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="text-[11px] uppercase tracking-wide text-gray-500">
                              Email
                            </div>
                            <div className="mt-1 break-all text-sm text-gray-900">
                              {getBookingContactEmail(s) || "-"}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="text-[11px] uppercase tracking-wide text-gray-500">
                              Phone
                            </div>
                            <div className="mt-1 text-sm text-gray-900">
                              {getBookingContactPhone(s) || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function newBlankSite() {
  return {
    id: cryptoRandomId(),
    label: "",
    address: "",
    projectName: "",
    poNumber: "",
    siteAccessNotes: "",
    bookingContactName: "",
    bookingContactEmail: "",
    bookingContactPhone: "",
  };
}

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const arr = new Uint8Array(8);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("\n");
  }
  return Math.random().toString(36).slice(2, 10);
}

function normalizeCustomerForSave(c, companyId) {
  const sites = (Array.isArray(c.sites) ? c.sites : [])
    .map((s) => ({
      id: s.id || cryptoRandomId(),
      label: (s.label || "").trim(),
      address: (s.address || "").trim(),
      projectName: getProjectName(s),
      poNumber: getPoNumber(s),
      siteAccessNotes: getSiteAccessNotes(s),
      bookingContactName: getBookingContactName(s),
      bookingContactEmail: getBookingContactEmail(s),
      bookingContactPhone: getBookingContactPhone(s),
    }))
    .filter(
      (s) =>
        s.label ||
        s.address ||
        s.projectName ||
        s.poNumber ||
        s.siteAccessNotes ||
        s.bookingContactName ||
        s.bookingContactEmail ||
        s.bookingContactPhone,
    );

  return {
    companyId: c.companyId || companyId || "",
    name: (c.name || "").trim(),
    email: (c.email || "").trim(),
    phone: (c.phone || "").trim(),
    accountName: getAccountName(c),
    accountsEmail: getAccountsEmail(c),
    address: (c.address || "").trim(),
    sites,
  };
}

function CustomerFormDialog({ initial, onClose, onSave }) {
  const [model, setModel] = useState(() => ({
    id: initial?.id,
    companyId: initial?.companyId || "",
    name: initial?.name || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    accountName: getAccountName(initial || {}),
    accountsEmail: getAccountsEmail(initial || {}),
    address: initial?.address || "",
    sites:
      Array.isArray(initial?.sites) && initial.sites.length
        ? initial.sites.map((s) => ({
            ...s,
            projectName: getProjectName(s),
            poNumber: getPoNumber(s),
            siteAccessNotes: getSiteAccessNotes(s),
            bookingContactName: getBookingContactName(s),
            bookingContactEmail: getBookingContactEmail(s),
            bookingContactPhone: getBookingContactPhone(s),
          }))
        : [newBlankSite()],
  }));
  const [siteFilter, setSiteFilter] = useState("");

  const updateField = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  const updateSite = (idx, k, v) =>
    setModel((m) => ({
      ...m,
      sites: m.sites.map((s, i) => (i === idx ? { ...s, [k]: v } : s)),
    }));

  const addSite = () => {
    setModel((m) => ({ ...m, sites: [newBlankSite(), ...m.sites] }));
    setSiteFilter("");
  };

  const removeSite = (idx) =>
    setModel((m) => ({ ...m, sites: m.sites.filter((_, i) => i !== idx) }));

  const filteredSiteIndexes = useMemo(() => {
    const q = siteFilter.trim().toLowerCase();
    if (!q) return model.sites.map((_, idx) => idx);

    return model.sites.reduce((acc, site, idx) => {
      if (getSiteSearchBlob(site).includes(q)) acc.push(idx);
      return acc;
    }, []);
  }, [model.sites, siteFilter]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div className="text-lg font-semibold">
            {model.id ? "Edit customer" : "New customer"}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm transition hover:bg-gray-50"
              onClick={addSite}
            >
              Add site
            </button>
            <button
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm transition hover:bg-gray-50"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>

        <div className="max-h-[80vh] space-y-5 overflow-auto p-4">
          <div className="rounded-xl border border-gray-300 bg-gray-50 p-4 shadow-sm">
            <div className="mb-3 text-base font-medium text-gray-900">Customer details</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input
                label="Customer name"
                value={model.name}
                onChange={(v) => updateField("name", v)}
                required
              />
              <Input
                label="Phone"
                value={model.phone}
                onChange={(v) => updateField("phone", v)}
              />
              <Input
                label="Email"
                value={model.email}
                onChange={(v) => updateField("email", v)}
              />
              <Input
                label="(Optional) Legacy single address"
                value={model.address}
                onChange={(v) => updateField("address", v)}
              />
            </div>
          </div>

          <div className="rounded-xl border border-gray-300 bg-gray-50 p-4 shadow-sm">
            <div className="mb-3 text-base font-medium text-gray-900">Accounts details</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input
                label="Account name"
                value={model.accountName}
                onChange={(v) => updateField("accountName", v)}
              />
              <Input
                label="Accounts email"
                value={model.accountsEmail}
                onChange={(v) => updateField("accountsEmail", v)}
              />
            </div>
          </div>

          <div className="mt-2 rounded-2xl border border-gray-300 bg-gradient-to-b from-gray-50 to-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-300 pb-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-base font-semibold text-gray-900">Site details</div>
                <div className="mt-1 text-sm text-gray-500">
                  Search by site label, address, project, PO, or booking contact.
                </div>
              </div>

              <div className="w-full sm:w-[360px]">
                <label className="block">
                  <div className="mb-1 text-xs font-medium text-gray-600">Find site</div>
                  <input
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    placeholder="Search sites inside this customer..."
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                  />
                </label>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold text-gray-900">{filteredSiteIndexes.length}</span> of{" "}
                <span className="font-semibold text-gray-900">{model.sites.length}</span> site{model.sites.length === 1 ? "" : "s"}
              </div>
              {!!siteFilter && (
                <button
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                  onClick={() => setSiteFilter("")}
                >
                  Clear search
                </button>
              )}
            </div>

            <div className="mt-4 space-y-4">
              {filteredSiteIndexes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-400 bg-white px-4 py-8 text-center text-sm text-gray-500">
                  No matching sites found.
                </div>
              ) : (
                filteredSiteIndexes.map((idx) => {
                  const s = model.sites[idx];
                  return (
                    <div
                      key={s.id}
                      className="overflow-hidden rounded-2xl border-2 border-gray-300 bg-white shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-3 border-b border-gray-300 bg-gray-100 px-4 py-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Site #{idx + 1}</div>
                          <div className="mt-0.5 text-xs text-gray-600">
                            {s.label?.trim() || "Unnamed site"}
                          </div>
                        </div>
                        {model.sites.length > 1 && (
                          <button
                            className="rounded-lg border border-gray-400 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                            onClick={() => removeSite(idx)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="space-y-5 p-4">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <Input
                            label="Site label"
                            value={s.label}
                            onChange={(v) => updateSite(idx, "label", v)}
                            required
                          />

                          <AddressAutocomplete
                            label="Site address"
                            value={s.address}
                            onChange={(v) => updateSite(idx, "address", v)}
                          />

                          <Input
                            label="Project name"
                            value={s.projectName || ""}
                            onChange={(v) => updateSite(idx, "projectName", v)}
                          />

                          <Input
                            label="PO"
                            value={s.poNumber || ""}
                            onChange={(v) => updateSite(idx, "poNumber", v)}
                          />

                          <TextArea
                            label="Site access notes (visible to driver)"
                            value={s.siteAccessNotes || ""}
                            onChange={(v) => updateSite(idx, "siteAccessNotes", v)}
                            rows={3}
                          />

                          <div />
                        </div>

                        <div className="rounded-xl border border-gray-300 bg-gray-50 p-4">
                          <div className="mb-3 text-sm font-semibold text-gray-800">
                            Booking contact details
                          </div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <Input
                              label="Booking contact name"
                              value={s.bookingContactName || ""}
                              onChange={(v) => updateSite(idx, "bookingContactName", v)}
                            />
                            <Input
                              label="Booking contact email"
                              value={s.bookingContactEmail || ""}
                              onChange={(v) => updateSite(idx, "bookingContactEmail", v)}
                            />
                            <Input
                              label="Booking contact phone"
                              value={s.bookingContactPhone || ""}
                              onChange={(v) => updateSite(idx, "bookingContactPhone", v)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4">
          <button
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm transition hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded-lg border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-black"
            onClick={() => onSave(model)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, required, type = "text" }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs text-gray-600">
        {label}
        {required ? " *" : ""}
      </div>
      <input
        type={type}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, required, rows = 4 }) {
  return (
    <label className="block md:col-span-2">
      <div className="mb-1 text-xs text-gray-600">
        {label}
        {required ? " *" : ""}
      </div>
      <textarea
        rows={rows}
        className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function AddressAutocomplete({ label, value, onChange, required }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    const g = window.google;
    if (!g?.maps?.places || !inputRef.current) return;

    autocompleteRef.current = new g.maps.places.Autocomplete(inputRef.current, {
      types: ["geocode"],
      fields: ["formatted_address", "geometry"],
    });

    const listener = autocompleteRef.current.addListener(
      "place_changed",
      () => {
        const place = autocompleteRef.current.getPlace();
        const addr = place?.formatted_address || inputRef.current.value || "";
        onChange(addr);
      },
    );

    return () => {
      g.maps.event.removeListener(listener);
    };
  }, [onChange]);

  return (
    <label className="block">
      <div className="mb-1 text-xs text-gray-600">
        {label}
        {required ? " *" : ""}
      </div>
      <input
        ref={inputRef}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Start typing an address..."
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
      />
    </label>
  );
}
